import React, { useEffect, useRef, useState } from "react";

import BoltButton from "../../../../../../../Components/UI/Buttons/BoltButton";
import { createRunDriver } from "../../../../../../../services/ShipperServices/DeliveryRoutes/deliveryRuns.services";

// The driver lists' last option, which opens this form.
export const NEW_DRIVER = "new-driver";

const FIELD =
  "min-w-[0px] rounded-[6px] border-[1px] border-ui-border-strong bg-ui-surface px-[9px] py-[6px] text-[12.5px] text-ui-text focus:border-brand focus:outline-none disabled:opacity-60";

// The API's own words when it refuses a driver, else a plain retry message.
const refusal = (error) => {
  const data = error?.response?.data;
  return (
    data?.name?.[0] ||
    data?.phone?.[0] ||
    data?.message ||
    "Couldn't add the driver. Try again."
  );
};

/**
 * Adds a driver from a run's driver list: a name and, when known, a phone.
 * The driver is saved to the account without a login and can be picked for
 * any run. `onCreated(driver)` gets the driver shaped like the driver list's
 * entries.
 */
const NewDriverForm = ({ onCreated, onCancel, className = "" }) => {
  const nameRef = useRef(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const save = async () => {
    if (saving || !name.trim()) return;
    setSaving(true);
    setError("");
    const response = await createRunDriver({
      name: name.trim(),
      phone: phone.trim() || null,
    });
    setSaving(false);
    const driver = response.error ? null : response?.data?.result;
    if (driver?.id == null) {
      setError(refusal(response.error));
      return;
    }
    onCreated(driver);
  };

  // Enter adds the driver (the form sits among the run's own fields).
  const onKeyDown = (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    save();
  };

  return (
    <div
      role="group"
      aria-label="New driver"
      className={`grid gap-[8px] rounded-[7px] border-[1px] border-ui-border bg-ui-surface-sunken p-[10px] ${className}`}
    >
      <div className="flex flex-wrap items-center gap-[8px]">
        <input
          ref={nameRef}
          aria-label="Driver's name"
          placeholder="Name"
          maxLength={100}
          value={name}
          disabled={saving}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={onKeyDown}
          className={`${FIELD} flex-[2_1_160px]`}
        />
        <input
          type="tel"
          aria-label="Driver's phone"
          placeholder="Phone (optional)"
          maxLength={30}
          value={phone}
          disabled={saving}
          onChange={(event) => setPhone(event.target.value)}
          onKeyDown={onKeyDown}
          className={`${FIELD} flex-[1_1_130px]`}
        />
        <div className="flex shrink-0 gap-[8px]">
          <BoltButton
            type="button"
            variant="outline"
            size="sm"
            label="Cancel"
            onClick={onCancel}
            disabled={saving}
          />
          <BoltButton
            type="button"
            variant="primary"
            size="sm"
            label={saving ? "Adding…" : "Add driver"}
            onClick={save}
            disabled={saving || !name.trim()}
          />
        </div>
      </div>
      {error ? (
        <p
          role="alert"
          className="m-[0px] text-[11.5px] text-state-danger-text"
        >
          {error}
        </p>
      ) : (
        <p className="m-[0px] text-[11px] text-ui-text-muted">
          Saved for future runs. No login needed.
        </p>
      )}
    </div>
  );
};

export default NewDriverForm;
